import React from 'react'
import cn from 'classnames'
import FormattedCurrency from 'js/components/FormattedCurrency'
import { FormattedMessage } from 'react-intl'
import CheckIcon from 'media/check-white.svg?svgr-webpack'

function getDurationFrequency({ duration, discount, price }) {
  if (duration === 1)
    return (
      <FormattedMessage
        id="upgrade.billed_monthly"
        defaultMessage="Billed monthly"
      />
    )
  if (duration === 3)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_quarterly"
        defaultMessage="Billed quarterly"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_quarterly"
        defaultMessage="Billed {price} quarterly"
        values={{ price }}
      />
    )
  if (duration === 6)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_twiceAYear"
        defaultMessage="Billed twice annually"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_twiceAYear"
        defaultMessage="Billed {price} twice annually"
        values={{ price }}
      />
    )
  if (duration === 12)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_yearly"
        defaultMessage="Billed annually"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_yearly"
        defaultMessage="Billed {price} annually"
        values={{ price }}
      />
    )
  if (duration === 24)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_everyOtherYear"
        defaultMessage="Billed every other year"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_everyOtherYear"
        defaultMessage="Billed {price} every other year"
        values={{ price }}
      />
    )
}

function getDurationInWords({ duration, price }) {
  if (duration === 1)
    return (
      <FormattedMessage
        id="upgrade.billed_first_month"
        defaultMessage="First month {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 3)
    return (
      <FormattedMessage
        id="upgrade.billed_first_quarter"
        defaultMessage="First quarter {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 6)
    return (
      <FormattedMessage
        id="upgrade.billed_first_half_year"
        defaultMessage="First six months {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 12)
    return (
      <FormattedMessage
        id="upgrade.billed_first_year"
        defaultMessage="First year {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 24)
    return (
      <FormattedMessage
        id="upgrade.billed_first_two_years"
        defaultMessage="First two years {price}"
        values={{
          price,
        }}
      />
    )
}

type DiscountedPriceProps = {
  originalPrice: number
  currency: string
}
const DiscountedPrice = ({ originalPrice, currency }: DiscountedPriceProps) => (
  <span className="strikethrough">
    <FormattedCurrency value={originalPrice} currency={currency} />
  </span>
)

type Props = {
  price: number
  pricePerMonth: number
  originalPrice: number
  duration: 1 | 3 | 6 | 12 | 24
  selected: boolean
  onClick: () => void
  currency: string
  bestPlanInfo: any
  trialDuration: number
  onSelectProduct: (duration: 1 | 3 | 6 | 12 | 24) => void
  discount: number
  discountOnlyFirstPurchase: boolean
  isFallbackPrice: boolean
}

function ChooseSubscriptionProduct({
  price,
  pricePerMonth,
  originalPrice,
  duration,
  selected,
  onClick,
  currency,
  bestPlanInfo,
  trialDuration,
  onSelectProduct,
  discount,
  discountOnlyFirstPurchase = true,
  isFallbackPrice,
}: Props) {
  return (
    <div
      onClick={onClick}
      className={cn(
        `
        relative
        min-w-full
        flex flex-col justify-between items-stretch lg:items-center
        bg-white py-24 lg:py-12 lg:pt-12 px-16
        first:rounded-t-12 last:rounded-b-12 lg:rounded-12
        border-1 lg:border-0 -mb-1 lg:shadow-elevated
      `,
        selected ? 'border-Brand z-10' : 'border-Border'
      )}
      style={{
        boxShadow: selected
          ? 'inset 0 0 0 1px hsl(152, 58%, 53%)'
          : '0 0 0 1px rgba(0,0,0,0)',
      }}
    >
      <div className="flex justify-between lg:flex-col lg:mb-12 lg:px-16 lg:py-16 items-center text-left lg:text-center">
        <div className="flex mr-12 lg:mr-0">
          {bestPlanInfo}
          <div>
            <div className="type-17-demibold lg:type-17-demibold-caps lg:pb-4 relative lg:static flex items-center lg:justify-center">
              {duration}&nbsp;
              {duration === 1 ? (
                <FormattedMessage
                  id="Redeem.plans.month"
                  defaultMessage="month"
                />
              ) : (
                <FormattedMessage
                  id="Redeem.plans.months"
                  defaultMessage="months"
                />
              )}
            </div>
            <div className="type-small text-TypeSub lg:mb-32">
              {getDurationFrequency({
                duration,
                discount: Boolean(discount),
                price: <FormattedCurrency value={price} currency={currency} />,
              })}
            </div>
          </div>
        </div>

        <div className="text-right lg:text-center shrink-0 mb-auto">
          <div
            className={
              'text-20 lg:text-40 font-bold lg:mb-0 lg:px-16 text-right'
            }
            style={{ lineHeight: 1 }}
          >
            {
              <span
                className={cn({
                  'strikethrough strikethrough-tilted': Boolean(discount),
                })}
              >
                <FormattedCurrency
                  value={originalPrice / duration || pricePerMonth}
                  currency={currency}
                />
              </span>
            }
          </div>

          {Boolean(discount) && (
            <div className="text-17 lg:text-28 font-demibold  text-red inline-block text-right mr-4 lg:mr-0">
              {<FormattedCurrency value={pricePerMonth} currency={currency} />}
            </div>
          )}
          <div className="text-14 lg:text-17 text-TypeSub text-right lg:text-center inline-block lg:block">
            <span className="hidden lg:block">
              /
              <FormattedMessage
                id="Redeem.plans.month"
                defaultMessage="month"
              />
            </span>
            <span className="lg:hidden">
              /<FormattedMessage id="SignUp.shortMon" defaultMessage="mon" />
            </span>
          </div>
        </div>
      </div>

      <div className="text-left lg:text-center w-full">
        <div className="inline lg:block lg:mb-12 text-17">
          {Boolean(trialDuration) && (
            <span
              className="text-14 lg:text-17"
              data-uid="plan_free_trial_info"
            >
              <FormattedMessage
                defaultMessage="{numberOfDays}-day free trial included"
                id="free_trial_50_check_2"
                values={{
                  numberOfDays: trialDuration,
                }}
              />
            </span>
          )}
          <div className="text-17 lg:block lg:mb-4 text-TypeSub">
            {discount > 0 && (
              <div className="text-BrandDark font-demibold text-14 lg:text-17 mt-6 lg:mt-0">
                {discountOnlyFirstPurchase ? (
                  getDurationInWords({
                    duration,
                    price: (
                      <DiscountedPrice
                        originalPrice={originalPrice}
                        currency={currency}
                      />
                    ),
                  })
                ) : (
                  <DiscountedPrice
                    originalPrice={originalPrice}
                    currency={currency}
                  />
                )}{' '}
                <FormattedCurrency value={price} currency={currency} />
              </div>
            )}
            {isFallbackPrice && (
              <span className="hidden lg:block">
                <FormattedMessage
                  id="chooseSubscription.regularPrice"
                  defaultMessage="Regular price"
                />
              </span>
            )}
          </div>
        </div>
        <button
          className={`
            hidden lg:block w-full
            type-14-demibold-caps text-white
            transition bg-Brand hover:bg-BrandDark
            rounded-8 py-16 mt-8
          `}
          onClick={() => onSelectProduct(duration)}
          data-uid={`plan_${duration}_months_button`}
          data-testid={`choose-button-${duration}`}
        >
          {duration === 1 ? (
            <FormattedMessage
              id="chooseSubscription.chooseButton_singular"
              defaultMessage="Choose {duration} month"
              values={{ duration }}
            />
          ) : (
            <FormattedMessage
              id="chooseSubscription.chooseButton_plural"
              defaultMessage="Choose {duration} months"
              values={{ duration }}
            />
          )}
        </button>
      </div>
      {selected && (
        <div
          className={cn('absolute lg:hidden pointer-events-none')}
          style={{
            top: -1,
            right: -1,
            bottom: -1,
            left: -1,
          }}
        >
          <div
            className={`
            flex absolute top-1/2 left-0 h-24 w-24
            transform -translate-y-1/2
            -ml-12 bg-Brand rounded-full
          `}
          >
            <CheckIcon className="m-auto transform scale-50" />
          </div>
        </div>
      )}
    </div>
  )
}

export default ChooseSubscriptionProduct
